import { useRef, useState } from 'react';
import Field from '../../components/common/Field';
import { ATTACH_ACCEPT, ATTACH_MAX_BYTES, attachTotal, fmtBytes } from '../../utils/attachments';

/* Attachments in Compose Email (spec/32). One dashed strip to add files (a
   drop target too), one row per file with its state, one meter. The state —
   uploading, ready, failed — lives on the row, so Send can be held without a
   modal. Removing needs no confirmation: it is a draft nobody has saved. */

export const IconPaperclip = () => (
  <svg viewBox="0 0 24 24"><path d="m21 11.5-8.6 8.6a5.5 5.5 0 0 1-7.8-7.8l8.6-8.6a3.7 3.7 0 0 1 5.2 5.2l-8.6 8.6a1.8 1.8 0 0 1-2.6-2.6l7.9-7.9" /></svg>
);

const IconX = () => <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18" /></svg>;

/* the real thumbnail for a photo, a coloured type label for the rest. Chrome
   can't draw a HEIC, so a thumbnail that fails leaves the label showing. */
export function AttachmentTile({ file }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className={`att-tile ${file.kind || ''}`}>
      {file.label}
      {file.thumb && !broken ? <img src={file.thumb} alt="" onError={() => setBroken(true)} /> : null}
    </span>
  );
}

/* the same files as compact chips: in the preview, and on a sent email, where
   each chip opens the stored copy */
export function AttachmentChips({ files, style }) {
  return (
    <div className="pv-files" style={style}>
      {files.map((f, i) =>
        f.url ? (
          <a className="pv-file" key={f.key || f.id || i} href={f.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
            <AttachmentTile file={f} />
            <span className="att-name">{f.name}</span>
            <span className="sz">{fmtBytes(f.size)}</span>
          </a>
        ) : (
          <div className="pv-file" key={f.key || f.id || i}>
            <AttachmentTile file={f} />
            <span className="att-name">{f.name}</span>
            <span className="sz">{fmtBytes(f.size)}</span>
          </div>
        )
      )}
    </div>
  );
}

export default function EmailAttachments({ attachments, rejects, onAdd, onRetry, onRemove, onDismissRejects }) {
  const inputRef = useRef(null);
  const btnRef = useRef(null);
  const listRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [live, setLive] = useState('');

  const hasFiles = (e) => e.dataTransfer && [...e.dataTransfer.types].includes('Files');

  const addFiles = (files) => {
    const added = onAdd(files);
    setLive(added.length ? `Attached ${added.map((a) => a.name).join(', ')}` : '');
  };

  /* focus moves to the next row's remove button, or back to Attach files when
     the list is empty, so a keyboard user never loses their place */
  const removeFile = (a) => {
    const i = onRemove(a.id);
    setLive(`Removed ${a.name}`);
    requestAnimationFrame(() => {
      const xs = listRef.current ? [...listRef.current.querySelectorAll('.att-x')] : [];
      (xs[Math.min(i, xs.length - 1)] || btnRef.current)?.focus();
    });
  };

  const total = attachTotal(attachments);
  const n = attachments.length;

  return (
    <Field label={<>Attachments <span className="opt-flag">optional</span></>} style={{ margin: '16px 0 0' }}>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        accept={ATTACH_ACCEPT}
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <div
        className={`attach-zone ${drag ? 'drag' : ''}`}
        onDragEnter={(e) => { if (hasFiles(e)) { e.preventDefault(); setDrag(true); } }}
        onDragOver={(e) => { if (hasFiles(e)) { e.preventDefault(); setDrag(true); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false); }}
        onDrop={(e) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          setDrag(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <button type="button" className="btn btn-ghost btn-sm" ref={btnRef} onClick={() => inputRef.current?.click()}>
          <IconPaperclip />
          Attach files
        </button>
        <span className="attach-hint">
          <span className="drop">Or drop them here. </span>Photos, PDFs, documents and short videos, up to 25 MB in total.
        </span>
      </div>

      {rejects.length > 0 && (
        <div className="attach-note" role="alert">
          <ul>
            {rejects.map((r, i) => (
              <li key={i}><b>{r.name}</b> was not added: {r.why}.</li>
            ))}
          </ul>
          <button type="button" aria-label="Dismiss" onClick={() => { onDismissRejects(); btnRef.current?.focus(); }}>
            <IconX />
          </button>
        </div>
      )}

      {n > 0 && (
        <div className="attach-list" ref={listRef}>
          {attachments.map((a) => (
            <div className={`att-row ${a.state === 'failed' ? 'failed' : ''}`} key={a.id}>
              <AttachmentTile file={a} />
              <div className="att-main">
                <div className="att-name" title={a.name}>{a.name}</div>
                {a.state === 'uploading' && (
                  <>
                    <div className="att-sub">
                      <span>Uploading {a.pct}%</span>
                      <span>{fmtBytes(a.size)}</span>
                    </div>
                    <div className="att-bar"><i style={{ width: `${a.pct}%` }} /></div>
                  </>
                )}
                {a.state === 'failed' && (
                  <div className="att-sub err">
                    Could not upload{' '}
                    <button type="button" className="att-retry" onClick={() => onRetry(a.id)}>Retry</button>
                  </div>
                )}
                {a.state === 'ready' && <div className="att-sub">{fmtBytes(a.size)}</div>}
              </div>
              <button type="button" className="att-x" aria-label={`Remove ${a.name}`} onClick={() => removeFile(a)}>
                <IconX />
              </button>
            </div>
          ))}
        </div>
      )}

      {n > 0 && (
        <div className={`attach-meter ${total >= ATTACH_MAX_BYTES * 0.8 ? 'near' : ''}`}>
          <span>
            <b>{n} file{n === 1 ? '' : 's'}</b> · {fmtBytes(total)} of 25 MB
          </span>
          <span className="bar"><i style={{ width: `${Math.min(100, (100 * total) / ATTACH_MAX_BYTES)}%` }} /></span>
        </div>
      )}

      <div className="hint" role="status" aria-live="polite" style={{ position: 'absolute', left: -9999 }}>{live}</div>
    </Field>
  );
}
